import type { CSSProperties } from 'react'
import { Button, Eyebrow } from './atoms'
import { useLang, t } from '../lib/lang'
import { externalUrl } from '../lib/api'
import type { ContattiRow, Lang } from '../lib/types'

const inputStyle: CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15,
  padding: '14px 0', background: 'transparent',
  color: 'var(--fg-inverse)', border: 'none',
  borderBottom: '1px solid var(--line-inverse)',
  outline: 'none', width: '100%', boxSizing: 'border-box',
}

function telHref(phone: string | null): string | undefined {
  if (!phone) return undefined
  const cleaned = phone.replace(/\s+/g, '')
  return cleaned.startsWith('+') ? `tel:${cleaned}` : `tel:+39${cleaned}`
}

function titleFor(lang: Lang): [string, string] {
  return [t(lang, 'contatti_title_a'), t(lang, 'contatti_title_b')]
}

export function Contatti({ contatti }: { contatti: ContattiRow | null }) {
  const { lang } = useLang()
  const [titleA, titleB] = titleFor(lang)

  function onMail(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (contatti?.email) window.location.href = `mailto:${contatti.email}`
  }

  return (
    <section id="contatti" className="ax-contatti" style={{
      background: 'var(--ink)', color: 'var(--fg-inverse)',
      padding: '120px 48px 48px', position: 'relative', overflow: 'hidden',
    }}>
      <div className="ax-grid-contatti" style={{
        maxWidth: 1280, margin: '0 auto',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80,
      }}>
        <div>
          <Eyebrow light>{t(lang, 'nav_contatti')}</Eyebrow>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            fontSize: 72, lineHeight: 0.98, margin: '20px 0 32px',
          }}>{titleA}<br />{titleB}</h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 16, lineHeight: 1.6,
            color: 'var(--fg-inverse-muted)', maxWidth: 420,
          }}>{t(lang, 'contatti_intro')}</p>

          {contatti && (
            <div style={{ marginTop: 48 }}>
              {contatti.referente && (
                <>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--fg-inverse-muted)', marginBottom: 8 }}>
                    {t(lang, 'label_referente')}
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 24, marginBottom: 24 }}>
                    {contatti.referente}
                  </div>
                </>
              )}

              <div className="ax-contatti-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                {contatti.email && (
                  <div>
                    <div style={labelStyle}>{t(lang, 'label_email')}</div>
                    <a href={`mailto:${contatti.email}`} style={linkOnDark}>{contatti.email}</a>
                  </div>
                )}
                {contatti.phone && (
                  <div>
                    <div style={labelStyle}>{t(lang, 'label_phone')}</div>
                    <a href={telHref(contatti.phone)} style={linkOnDark}>{contatti.phone}</a>
                  </div>
                )}
                <div>
                  <div style={labelStyle}>{t(lang, 'label_base')}</div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 15 }}>Bene Vagienna (CN)</div>
                </div>
                <div>
                  <div style={labelStyle}>{t(lang, 'label_social')}</div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {(() => {
                      const sp = externalUrl(contatti.spotify_url)
                      const ig = externalUrl(contatti.instagram_url)
                      const yt = externalUrl(contatti.youtube_url)
                      return (
                        <>
                          {sp && <a href={sp} target="_blank" rel="noopener noreferrer" style={socialLink}>Spotify</a>}
                          {sp && ig && <span style={{ color: 'var(--fg-inverse-muted)' }}>·</span>}
                          {ig && <a href={ig} target="_blank" rel="noopener noreferrer" style={socialLink}>Instagram</a>}
                          {ig && yt && <span style={{ color: 'var(--fg-inverse-muted)' }}>·</span>}
                          {yt && <a href={yt} target="_blank" rel="noopener noreferrer" style={socialLink}>YouTube</a>}
                        </>
                      )
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <form onSubmit={onMail} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <input placeholder={t(lang, 'form_name')} style={inputStyle} />
            <input placeholder={t(lang, 'form_email')} type="email" style={inputStyle} />
            <input placeholder={t(lang, 'form_when')} style={inputStyle} />
            <select defaultValue="" style={{ ...inputStyle, color: 'var(--fg-inverse-muted)' }}>
              <option value="" disabled>{t(lang, 'form_format_ph')}</option>
              <option>{t(lang, 'form_format_theatrical')}</option>
              <option>{t(lang, 'form_format_essential')}</option>
              <option>{t(lang, 'form_format_tbd')}</option>
            </select>
            <textarea rows={5} placeholder={t(lang, 'form_msg')} style={{ ...inputStyle, resize: 'none', fontFamily: 'var(--font-body)' }} />
            <div style={{ marginTop: 12 }}>
              <Button type="submit" variant="accent">{t(lang, 'form_submit')}</Button>
            </div>
          </form>
        </div>
      </div>

      <div className="ax-contatti-footer" style={{
        marginTop: 96, paddingTop: 32,
        borderTop: '1px solid var(--line-inverse)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 16, maxWidth: 1280,
        marginLeft: 'auto', marginRight: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <img src="/assets/logo-white-trans.png" alt="Ancreus" style={{ height: 42 }} />
        </div>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 11,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'var(--fg-inverse-muted)',
        }}>{t(lang, 'footer_rights')}</div>
      </div>
    </section>
  )
}

const labelStyle: CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 11,
  letterSpacing: '0.2em', textTransform: 'uppercase',
  color: 'var(--fg-inverse-muted)', marginBottom: 6,
}
const linkOnDark: CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 15,
  color: 'var(--fg-inverse)', textDecoration: 'none',
  borderBottom: '1px solid var(--fg-inverse)', paddingBottom: 2,
}
const socialLink: CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13,
  color: 'var(--fg-inverse)', textDecoration: 'none',
  letterSpacing: '0.14em', textTransform: 'uppercase',
}
